import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Download, Eye, RefreshCw, Search, X } from "lucide-react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../../api/api";
import Heading from "../../components/ui/Heading";
import { useAuth } from "../../context/AuthContext";
import { getSessionUsername } from "../../utils/sessionUser";

const currency = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const today = () => new Date().toISOString().split("T")[0];

const firstDayOfMonth = () => {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().split("T")[0];
};

const safeFileName = (value, fallback = "report") =>
  String(value || fallback).replace(/[\\/:*?"<>|]/g, "").trim() || fallback;

const normalizeStatementRow = (row) => ({
  ...row,
  vid: row.vid ?? row.Vid,
  vcode: row.vcode ?? row.VCode,
  vname: row.vname ?? row.VName,
  mobile: row.mobile ?? row.Mobile,
  opbalance: row.opbalance ?? row.opBalance,
  cashpayments: row.cashpayments ?? row.CashPayments,
  bankpayments: row.bankpayments ?? row.BankPayments,
  OtherPayments: row.OtherPayments ?? row.otherPayments,
});

const StatCard = ({ label, value, tone = "emerald" }) => {
  const tones = {
    emerald: "border-emerald-100 bg-emerald-50 text-emerald-950",
    sky: "border-sky-100 bg-sky-50 text-sky-950",
    rose: "border-rose-100 bg-rose-50 text-rose-950",
    slate: "border-slate-100 bg-white text-slate-950",
  };

  return (
    <div className={`rounded-2xl border p-4 shadow-sm ${tones[tone]}`}>
      <p className="text-sm font-semibold opacity-70">{label}</p>
      <p className="mt-1 text-xl font-semibold tracking-normal">{value}</p>
    </div>
  );
};

const PartyStatement = ({
  title = "Party Statement",
  statementApi = "GetDailySalesStatement",
  ledgerVtype = "Customer",
  csvPrefix = "party-statement",
}) => {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(firstDayOfMonth);
  const [toDate, setToDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerParty, setLedgerParty] = useState(null);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [ledgerSummary, setLedgerSummary] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerError, setLedgerError] = useState("");

  const fetchStatement = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api[statementApi](fromDate, toDate);
      setRows((data?.Table || []).map(normalizeStatementRow));
    } catch (err) {
      console.error(`Failed to fetch ${title}:`, err);
      setError(`Failed to load ${title.toLowerCase()}.`);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, statementApi, title, toDate]);

  useEffect(() => {
    fetchStatement();
  }, [fetchStatement]);

  const filteredRows = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return rows;

    return rows.filter((row) =>
      [row.vcode, row.vname, row.mobile, row.tag]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query))
    );
  }, [rows, searchTerm]);

  const totals = useMemo(
    () =>
      filteredRows.reduce(
        (acc, row) => {
          acc.opbalance += Number(row.opbalance || 0);
          acc.invValule += Number(row.invValule || 0);
          acc.cashpayments += Number(row.cashpayments || 0);
          acc.bankpayments += Number(row.bankpayments || 0);
          acc.OtherPayments += Number(row.OtherPayments || 0);
          acc.Balance += Number(row.Balance || 0);
          return acc;
        },
        {
          opbalance: 0,
          invValule: 0,
          cashpayments: 0,
          bankpayments: 0,
          OtherPayments: 0,
          Balance: 0,
        }
      ),
    [filteredRows]
  );

  const exportCsv = () => {
    const headers = [
      "Code",
      "Party",
      "Mobile",
      "Opening Balance",
      "Invoice Value",
      "Cash Payments",
      "Bank Payments",
      "Other Payments",
      "Balance",
      "Tag",
    ];
    const csvRows = filteredRows.map((row) => [
      row.vcode,
      row.vname,
      row.mobile,
      row.opbalance,
      row.invValule,
      row.cashpayments,
      row.bankpayments,
      row.OtherPayments,
      row.Balance,
      row.tag,
    ]);
    const csv = [headers, ...csvRows]
      .map((line) =>
        line
          .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${csvPrefix}-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportStatementPdf = () => {
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text(title, 14, 14);
    doc.setFontSize(9);
    doc.text(`${fromDate} to ${toDate}`, 14, 20);

    autoTable(doc, {
      startY: 25,
      head: [[
        "Code",
        "Party",
        "Mobile",
        "Opening Balance",
        "Invoice Value",
        "Cash Payments",
        "Bank Payments",
        "Other Payments",
        "Balance",
        "Tag",
      ]],
      body: filteredRows.map((row) => [
        row.vcode || "",
        row.vname || "",
        row.mobile || "",
        currency(row.opbalance),
        currency(row.invValule),
        currency(row.cashpayments),
        currency(row.bankpayments),
        currency(row.OtherPayments),
        currency(row.Balance),
        row.tag || "",
      ]),
      foot: [[
        "Total",
        "",
        "",
        currency(totals.opbalance),
        currency(totals.invValule),
        currency(totals.cashpayments),
        currency(totals.bankpayments),
        currency(totals.OtherPayments),
        currency(totals.Balance),
        "",
      ]],
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
      footStyles: { fillColor: [15, 23, 42] },
    });

    doc.save(`${csvPrefix}-${fromDate}-to-${toDate}.pdf`);
  };

  const openAccountLedger = async (row) => {
    setLedgerOpen(true);
    setLedgerParty(row);
    setLedgerRows([]);
    setLedgerSummary(null);
    setLedgerError("");
    setLedgerLoading(true);

    try {
      const res = await api.get("AccountLedgercuswiseAPI", {
        Vtype: ledgerVtype,
        Vid: row.vid,
        FromDate: fromDate,
        ToDate: toDate,
        Sessid: "9944010155",
        Usrname: user?.UsrName || getSessionUsername(),
        PrjId: 0,
        Rptformat: "Summary",
      });
      const data = res?.AccountLedgercuswiseAPIjs || res;
      setLedgerRows(data?.Table || []);
      setLedgerSummary(data?.Table1?.[0] || null);
    } catch (err) {
      console.error("Failed to fetch account ledger:", err);
      setLedgerError("Failed to load account summary.");
    } finally {
      setLedgerLoading(false);
    }
  };

  const closeAccountLedger = () => {
    setLedgerOpen(false);
    setLedgerParty(null);
    setLedgerRows([]);
    setLedgerSummary(null);
    setLedgerError("");
  };

  const exportLedgerExcel = () => {
    if (!ledgerRows.length && !ledgerSummary) return;

    const partyName = ledgerParty?.vname || "Account";
    const safePartyName = partyName.replace(/[\\/:*?"<>|]/g, "").trim() || "Account";
    const summaryRows = [
      ["Account Summary"],
      ["Party", partyName],
      ["From Date", fromDate],
      ["To Date", toDate],
      [],
      ["Invoice", "Receipt", "Balance"],
      [
        Number(ledgerSummary?.Invoice || 0),
        Number(ledgerSummary?.Receipt || 0),
        Number(ledgerSummary?.Balance || 0),
      ],
      [],
      ["Date", "Ref No", "Type", "Particulars", "Invoice", "Receipt", "Balance"],
      ...ledgerRows.map((item) => [
        item.RefDate || "",
        item.RefNo || "",
        item.RefType || "",
        item.Particulars || "",
        Number(item.Invoice || 0),
        Number(item.Receipt || 0),
        Number(item.Balance || 0),
      ]),
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(summaryRows);
    worksheet["!cols"] = [
      { wch: 14 },
      { wch: 18 },
      { wch: 16 },
      { wch: 36 },
      { wch: 14 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Account Summary");
    XLSX.writeFile(workbook, `${csvPrefix}-account-summary-${safePartyName}-${fromDate}-to-${toDate}.xlsx`);
  };

  const exportLedgerPdf = () => {
    if (!ledgerRows.length && !ledgerSummary) return;

    const partyName = ledgerParty?.vname || "Account";
    const doc = new jsPDF({ orientation: "landscape" });

    doc.setFontSize(14);
    doc.text("Account Summary", 14, 14);
    doc.setFontSize(9);
    doc.text(`${partyName} | ${fromDate} to ${toDate}`, 14, 20);

    if (ledgerSummary) {
      autoTable(doc, {
        startY: 25,
        head: [["Invoice", "Receipt", "Balance"]],
        body: [[
          currency(ledgerSummary.Invoice),
          currency(ledgerSummary.Receipt),
          currency(ledgerSummary.Balance),
        ]],
        theme: "grid",
        styles: { fontSize: 9 },
        headStyles: { fillColor: [15, 23, 42] },
      });
    }

    autoTable(doc, {
      startY: ledgerSummary && doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : 25,
      head: [["Date", "Ref No", "Type", "Particulars", "Invoice", "Receipt", "Balance"]],
      body: ledgerRows.map((item) => [
        item.RefDate || "",
        item.RefNo || "",
        item.RefType || "",
        item.Particulars || "",
        currency(item.Invoice),
        currency(item.Receipt),
        currency(item.Balance),
      ]),
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [15, 23, 42] },
    });

    doc.save(`${csvPrefix}-account-summary-${safeFileName(partyName, "Account")}-${fromDate}-to-${toDate}.pdf`);
  };

  useEffect(() => {
    if (!ledgerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeAccountLedger();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [ledgerOpen]);

  return (
    <div className="min-h-screen">
      <div className="mx-auto space-y-6 max-w-7xl">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <Heading>{title}</Heading>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_1fr_1.4fr_auto_auto_auto] md:items-end">
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-600">From Date</label>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                className="w-full p-3 border outline-none rounded-xl border-slate-200 text-slate-800"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-600">To Date</label>
              <input
                type="date"
                value={toDate}
                onChange={(event) => setToDate(event.target.value)}
                className="w-full p-3 border outline-none rounded-xl border-slate-200 text-slate-800"
              />
            </div>
            <div>
              <label className="block mb-1 text-sm font-medium text-slate-600">Search</label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3.5 h-5 w-5 text-emerald-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Party, code, mobile..."
                  className="w-full p-3 pl-10 border outline-none rounded-xl border-slate-200 text-slate-800"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={fetchStatement}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-teal-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              {loading ? "Loading" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportCsv}
              disabled={filteredRows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-slate-900 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
            <button
              type="button"
              onClick={exportStatementPdf}
              disabled={filteredRows.length === 0}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-rose-600 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              PDF
            </button>
          </div>
        </div>

        {error && (
          <div className="p-4 font-semibold border rounded-2xl border-rose-100 bg-rose-50 text-rose-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Parties" value={filteredRows.length} tone="slate" />
          <StatCard label="Invoice Value" value={currency(totals.invValule)} tone="emerald" />
          <StatCard
            label="Total Payments"
            value={currency(totals.cashpayments + totals.bankpayments + totals.OtherPayments)}
            tone="sky"
          />
          <StatCard label="Balance" value={currency(totals.Balance)} tone="rose" />
        </div>

        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="text-xs uppercase bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold text-left">Code</th>
                  <th className="px-4 py-3 font-semibold text-left">Party</th>
                  <th className="px-4 py-3 font-semibold text-left">Mobile</th>
                  <th className="px-4 py-3 font-semibold text-right">Opening</th>
                  <th className="px-4 py-3 font-semibold text-right">Invoice</th>
                  <th className="px-4 py-3 font-semibold text-right">Cash</th>
                  <th className="px-4 py-3 font-semibold text-right">Bank</th>
                  <th className="px-4 py-3 font-semibold text-right">Other</th>
                  <th className="px-4 py-3 font-semibold text-right">Balance</th>
                  <th className="px-4 py-3 font-semibold text-left">Tag</th>
                  <th className="px-4 py-3 font-semibold text-center">View</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 font-semibold text-center text-slate-500">
                      Loading {title.toLowerCase()}...
                    </td>
                  </tr>
                ) : filteredRows.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-12 font-semibold text-center text-slate-500">
                      No {title.toLowerCase()} records found.
                    </td>
                  </tr>
                ) : (
                  filteredRows.map((row) => (
                    <tr key={`${row.vid}-${row.vcode}-${row.mobile}`} className="hover:bg-emerald-50/60">
                      <td className="px-4 py-3 font-semibold whitespace-nowrap text-slate-700">{row.vcode}</td>
                      <td className="px-4 py-3 font-semibold whitespace-nowrap text-slate-900">{row.vname}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-slate-600">{row.mobile || "-"}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-slate-600">{currency(row.opbalance)}</td>
                      <td className="px-4 py-3 font-semibold text-right whitespace-nowrap text-slate-900">{currency(row.invValule)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-slate-600">{currency(row.cashpayments)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-slate-600">{currency(row.bankpayments)}</td>
                      <td className="px-4 py-3 text-right whitespace-nowrap text-slate-600">{currency(row.OtherPayments)}</td>
                      <td className="px-4 py-3 font-semibold text-right whitespace-nowrap text-rose-700">{currency(row.Balance)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-bold uppercase text-emerald-700">
                          {row.tag || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openAccountLedger(row)}
                          className="inline-flex items-center justify-center gap-1 rounded-lg bg-teal-50 px-3 py-1.5 text-xs font-bold text-teal-700 transition hover:bg-teal-100"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {filteredRows.length > 0 && (
                <tfoot className="text-white bg-slate-900">
                  <tr>
                    <td className="px-4 py-3 font-bold" colSpan="3">
                      Total
                    </td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.opbalance)}</td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.invValule)}</td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.cashpayments)}</td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.bankpayments)}</td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.OtherPayments)}</td>
                    <td className="px-4 py-3 font-bold text-right">{currency(totals.Balance)}</td>
                    <td className="px-4 py-3" colSpan="2" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>

      {ledgerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-4">
          <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Account Summary</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {ledgerParty?.vname || "-"} | {fromDate} to {toDate}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={exportLedgerExcel}
                  disabled={ledgerLoading || (!!ledgerError) || (!ledgerRows.length && !ledgerSummary)}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  Excel
                </button>
                <button
                  type="button"
                  onClick={exportLedgerPdf}
                  disabled={ledgerLoading || (!!ledgerError) || (!ledgerRows.length && !ledgerSummary)}
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Download className="h-4 w-4" />
                  PDF
                </button>
                <button
                  type="button"
                  onClick={closeAccountLedger}
                  className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
                  aria-label="Close account summary"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-5">
              {ledgerLoading ? (
                <div className="py-12 text-center font-semibold text-slate-500">Loading account summary...</div>
              ) : ledgerError ? (
                <div className="rounded-xl border border-rose-100 bg-rose-50 p-4 font-semibold text-rose-700">
                  {ledgerError}
                </div>
              ) : (
                <>
                  {ledgerSummary && (
                    <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <StatCard label="Invoice" value={currency(ledgerSummary.Invoice)} tone="emerald" />
                      <StatCard label="Receipt" value={currency(ledgerSummary.Receipt)} tone="sky" />
                      <StatCard label="Balance" value={currency(ledgerSummary.Balance)} tone="rose" />
                    </div>
                  )}

                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="min-w-full text-sm">
                      <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Date</th>
                          <th className="px-4 py-3 text-left font-semibold">Ref No</th>
                          <th className="px-4 py-3 text-left font-semibold">Type</th>
                          <th className="px-4 py-3 text-left font-semibold">Particulars</th>
                          <th className="px-4 py-3 text-right font-semibold">Invoice</th>
                          <th className="px-4 py-3 text-right font-semibold">Receipt</th>
                          <th className="px-4 py-3 text-right font-semibold">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {ledgerRows.length === 0 ? (
                          <tr>
                            <td colSpan="7" className="px-4 py-10 text-center font-semibold text-slate-500">
                              No account summary records found.
                            </td>
                          </tr>
                        ) : (
                          ledgerRows.map((item, index) => (
                            <tr key={`${item.Cpid || index}-${item.RefNo || ""}`} className="hover:bg-slate-50">
                              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{item.RefDate || "-"}</td>
                              <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-800">{item.RefNo || "-"}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-slate-600">{item.RefType || "-"}</td>
                              <td className="px-4 py-3 text-slate-700">{item.Particulars || "-"}</td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-slate-900">
                                {currency(item.Invoice)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right text-slate-600">
                                {currency(item.Receipt)}
                              </td>
                              <td className="whitespace-nowrap px-4 py-3 text-right font-semibold text-rose-700">
                                {currency(item.Balance)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartyStatement;
